import { Fragment, useContext, useEffect, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Formik } from "formik";
import { AuthContext } from "../../provider/AuthProvider";

import { Center } from "../../components/StyledComponents";
import Grid from "../../components/Grid";
import Button from "../../components/Button";
import Form from "../../components/Form";
import Input from "../../components/Form/Input";
import Submit from "../../components/Form/Submit";
import ErrorMessage from "../../components/Form/ErrorMessage";
import { MfaConfig } from "../../components/MfaModals";

import {
  schema as signInSchema,
  initialValues as signInInitialValues,
} from "../../constants/form/auth/sign-in";

import {
  schema as recoveryCodeSchema,
  initialValues as recoveryCodeInitialValues,
} from "../../constants/form/auth/recovery-code";

import {
  schema as passwordRecoverySchema,
  initialValues as passwordRecoveryInitialValues,
} from "../../constants/form/auth/password-recovery";

import {
  recoverPassword,
  resetPassword,
  signIn,
} from "../../store/actions/auth";

import { RESET_PASSWORD_CLEAR_ERROR } from "../../store/types/auth";

interface CustomizedState {
  siginStates: {
    loading: boolean;
    success: boolean;
    error: any;
  };
  from: {
    pathname: string;
    search: string;
    hash: string;
  };
}

// MFA validation schemas
const mfaTokenSchema = Yup.object().shape({
  token: Yup.string()
    .required("Este campo es requerido")
    .length(6, "El código debe tener 6 dígitos")
    .matches(/^[0-9]+$/, "Solo se permiten números"),
});

const mfaBackupSchema = Yup.object().shape({
  backupCode: Yup.string()
    .required("Este campo es requerido")
    .min(1, "Debe ingresaR el código de respaldo"),
});

const Page = ({
  userStates,
  authStates,
  userInformation,
  recoverPasswordStates,
  recoverPasswordInformation,
  resetPasswordStates,
}) => {
  let navigate = useNavigate();
  let location = useLocation();
  let { isAuthenticated } = useContext(AuthContext);
  let from = (location.state as CustomizedState)?.from?.pathname || "/";

  const dispatch = useDispatch();

  const { loading: userLoading } = userStates;
  const { error: authError, loading: authLoading, data: authData } = authStates;

  // Check URL params for password recovery flow
  const searchParams = new URLSearchParams(location.search);
  const emailFromUrl = searchParams.get('email') || '';
  const codeFromUrl = searchParams.get('code') || '';
  const verificationFromUrl = searchParams.get('verification') || '';
  const isRecoveryUrl = !!(emailFromUrl && codeFromUrl && verificationFromUrl);

  const [showLogin, setShowLogin] = useState(!isRecoveryUrl);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(isRecoveryUrl);
  const [accountToRecover, setAccountToRecover] = useState(emailFromUrl);
  const [passwordResetFormActions, setPasswordResetFormActions] = useState<any>(null);

  // MFA States
  const [showMfaChallenge, setShowMfaChallenge] = useState(false);
  const [showMfaRecovery, setShowMfaRecovery] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaBackupCode, setMfaBackupCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState<any>(null);
  const [openMfaConfig, setOpenMfaConfig] = useState(false);
  const [userInfoForMfa, setUserInfoForMfa] = useState<any>(null);

  // Error States
  const [lockoutMessage, setLockoutMessage] = useState("");
  const [passwordExpiredMessage, setPasswordExpiredMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordRecentlyUsed, setPasswordRecentlyUsed] = useState(false);

  // Refs for autofocus
  const loginEmailRef = useRef<HTMLInputElement>(null);
  const mfaTokenRef = useRef<HTMLInputElement>(null);
  const mfaBackupRef = useRef<HTMLInputElement>(null);
  const recoveryEmailRef = useRef<HTMLInputElement>(null);
  const changePasswordCodeRef = useRef<HTMLInputElement>(null);

  // Track shown toasts to prevent duplicates
  const shownToastsRef = useRef<Set<string>>(new Set());
  
  // Track last processed response content to prevent reprocessing
  const lastProcessedAuthDataContentRef = useRef<string>("");
  const lastProcessedAuthErrorContentRef = useRef<string>("");
  
  // Ref to access current credentials inside useEffect without adding as dependency
  const loginCredentialsRef = useRef(loginCredentials);
  
  // Update ref whenever credentials change
  useEffect(() => {
    loginCredentialsRef.current = loginCredentials;
  }, [loginCredentials]);

  const browserHash = localStorage.getItem("browserHash") || "";

  const appSignIn = ({ values, actions }) => {
    setLoginCredentials(values);
    // Backup credentials in sessionStorage for MFA flow
    sessionStorage.setItem('mfaLoginCredentials', JSON.stringify(values));
    setErrorMessage("");
    setLockoutMessage("");
    setPasswordExpiredMessage("");
    // Clear toast tracking for new login attempt
    shownToastsRef.current.clear();
    toast.dismiss();
    // Clear last processed content so new responses will be handled
    lastProcessedAuthDataContentRef.current = "";
    lastProcessedAuthErrorContentRef.current = "";

    // Attach browser hash if exists
    const loginData = {
      ...values,
      browser_hash: browserHash || undefined,
    };

    dispatch(signIn(loginData));
  };

  const handleMfaSubmit = ({ values, actions }) => {
    setErrorMessage("");
    setLockoutMessage("");
    // Clear toast tracking for new MFA attempt
    shownToastsRef.current.clear();
    toast.dismiss();
    
    // Get credentials from state or sessionStorage backup
    let credentials = loginCredentials;
    if (!credentials || !credentials.email || !credentials.password) {
      const stored = sessionStorage.getItem('mfaLoginCredentials');
      if (stored) {
        credentials = JSON.parse(stored);
        setLoginCredentials(credentials);
      }
    }
    
    // Ensure we have credentials
    if (!credentials || !credentials.email || !credentials.password) {
      setErrorMessage("Error: credenciales no disponibles. Por favor, intenta iniciar sesión nuevamente.");
      setShowMfaChallenge(false);
      setShowLogin(true);
      return;
    }
    
    const loginData = {
      email: credentials.email,
      password: credentials.password,
      twofa: showMfaRecovery ? undefined : values.token,
      backupCode: showMfaRecovery ? values.backupCode : undefined,
      browser_hash: rememberDevice && !showMfaRecovery ? "generate" : (browserHash || undefined),
    };

    // Store rememberDevice preference before login completes
    if (rememberDevice && !showMfaRecovery) {
      localStorage.setItem("rememberDevice", "true");
    }

    dispatch(signIn(loginData));
  };

  const sendRecoveryCode = ({ values, actions }) => {
    setErrorMessage("");
    // Clear toast tracking for new recovery attempt
    shownToastsRef.current.clear();
    toast.dismiss();
    dispatch(recoverPassword(values));
    setAccountToRecover(values.email);
  };

  const changePassword = ({ values, actions }) => {
    setPasswordResetFormActions(actions);
    // Clear toast tracking for new password reset attempt
    shownToastsRef.current.clear();
    toast.dismiss();
    dispatch(
      resetPassword({
        ...values,
        verification: verificationFromUrl || recoverPasswordInformation?.result?.verification,
      })
    );
  };

  // Handle different authentication responses
  useEffect(() => {
    if (authData) {
      // Create unique identifier for this response
      // Do NOT use Date.now() as fallback, as it causes re-triggers on re-renders
      const responseContent = JSON.stringify({
        code: authData.httpCode || authData.status,
        message: authData.message,
        timestamp: authData.timestamp
      });
      
      // Skip if we've already processed this exact response
      if (lastProcessedAuthDataContentRef.current === responseContent) {
        return;
      }
      
      const code = authData.httpCode || authData.status;
      const message = authData.message || "";
      
      // Check if this is a 202 response (even if httpCode is missing)
      if (code === 202 || (message && message.includes("respaldo"))) {
        toast.info("Configure nuevamente la autenticación de dos factores");
        setShowMfaChallenge(false);
        setShowMfaRecovery(false);
        setMfaToken("");
        setMfaBackupCode("");
        setShowLogin(true);
        // Open MFA config modal with user credentials
        setOpenMfaConfig(true);
        setUserInfoForMfa(loginCredentialsRef.current);
        return;
      }
      
      // Mark this response as processed
      lastProcessedAuthDataContentRef.current = responseContent;
    }
  }, [authData]);

  // Handle authentication errors
  useEffect(() => {
    if (authError && authError.httpCode) {
      // Create unique identifier for this error
      // Do NOT use Date.now() as fallback, as it causes re-triggers on re-renders
      const errorContent = JSON.stringify({
        code: authError.httpCode,
        message: authError.message,
        timestamp: authError.timestamp
      });
      
      // Skip if we've already processed this exact error
      if (lastProcessedAuthErrorContentRef.current === errorContent) {
        return;
      }
      
      const code = authError.httpCode;
      const message = authError.message || "";

      if (code === 428) {
        // MFA challenge required (from error path)
        // If we're already on MFA challenge, this is an invalid code error
        if (showMfaChallenge) {
          const errorMsg = message || (showMfaRecovery ? "Código de respaldo incorrecto" : "Código de verificación incorrecto");
          setErrorMessage(errorMsg);
          // Toast is already shown in authData handler above - skip duplicate here
          // Stay on MFA challenge screen
        } else {
          // First time MFA challenge
          setShowLogin(false);
          setShowMfaChallenge(true);
          setShowMfaRecovery(false);
        }
      } else if (code === 423) {
        // Account locked
        const errorMsg = message || "Tu cuenta ha sido bloqueada debido a múltiples intentos fallidos.";
        setLockoutMessage(errorMsg);
        
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }

        setShowMfaChallenge(false);
        setShowLogin(true);
      } else if (code === 403) {
        // MFA not configured
        const errorMsg = message || "Habilite su autenticación 2FA para ingresar";
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.warning(errorMsg);
        }
        setOpenMfaConfig(true);
        setUserInfoForMfa(loginCredentialsRef.current);
        return;
      } else if (code === 202) {
        // MFA temporarily disabled after backup code use - open MFA config
        toast.info("Configure nuevamente la autenticación de dos factores");
        setShowMfaChallenge(false);
        setShowMfaRecovery(false);
        setMfaToken("");
        setMfaBackupCode("");
        setShowLogin(true);
        // Open MFA config modal with user credentials
        setOpenMfaConfig(true);
        setUserInfoForMfa(loginCredentialsRef.current);
      } else if (code === 401) {
        // Check if it's password expired
        if (message.includes("PASSWORD_EXPIRED") || message.includes("contraseña ha expirado")) {
          setPasswordExpiredMessage(
            message || "Tu contraseña ha expirado. Por favor, restablece tu contraseña para continuar."
          );
          setShowLogin(false);
          setShowPasswordRecovery(true);
        } else if (showMfaChallenge) {
          // Invalid MFA code or backup code - stay on MFA challenge screen
          const errorMsg = showMfaRecovery ? "Código de respaldo incorrecto" : "Código de verificación incorrecto";
          setErrorMessage(errorMsg);
          if (!shownToastsRef.current.has(errorMsg)) {
            shownToastsRef.current.add(errorMsg);
            toast.error(errorMsg);
          }
          // Don't redirect to login, stay on MFA challenge
        } else {
          // Generic 401 error (wrong credentials)
          const errorMsg = message || "Usuario o Contraseña incorrecta";
          setErrorMessage(errorMsg);
          if (!shownToastsRef.current.has(errorMsg)) {
            shownToastsRef.current.add(errorMsg);
            toast.error(errorMsg);
          }
        }
      } else if (code === 400 && message.includes("PASSWORD_RECENTLY_USED")) {
        // Password reused
        setErrorMessage(
          "No puedes usar una contraseña que ya has utilizado recientemente. Por favor, elige una contraseña diferente."
        );
      } else if (authError) {
        // Generic error fallback
        const errorMsg = message || "Usuario o Contraseña incorrecta";
        setErrorMessage(errorMsg);
        // Show toast for MFA errors
        if (showMfaChallenge) {
          const mfaErrorMsg = showMfaRecovery ? "Código de respaldo incorrecto" : "Código de verificación incorrecto";
          if (!shownToastsRef.current.has(mfaErrorMsg)) {
            shownToastsRef.current.add(mfaErrorMsg);
            toast.error(mfaErrorMsg);
          }
          // Stay on MFA challenge screen
        } else {
          // Redirect to login for other errors
          setShowLogin(true);
          setShowPasswordRecovery(false);
          setShowMfaChallenge(false);
        }
      }
      
      // Mark this error as processed
      lastProcessedAuthErrorContentRef.current = errorContent;
    }
  }, [authError]);

  useEffect(() => {
    if (recoverPasswordStates.success) {
      toast.success("Si tu email está registrado, te enviamos un código.");
      setShowLogin(true);
      setShowPasswordRecovery(false);
      setShowPasswordChange(false);
      setErrorMessage("");
    }
  }, [recoverPasswordStates.success]);

  // Handle password recovery errors - always show ambiguous message
  useEffect(() => {
    if (recoverPasswordStates.error) {
      // Always show ambiguous message, never reveal if user exists or not
      toast.success("Si tu email está registrado, te enviamos un código.");
      setShowLogin(true);
      setShowPasswordRecovery(false);
      setShowPasswordChange(false);
      setErrorMessage("");
    }
  }, [recoverPasswordStates.error]);

  useEffect(() => {
    if (resetPasswordStates.success) {
      setShowLogin(true);
      setShowPasswordRecovery(false);
	// @ts-ignore
      setShowPasswordChange(false);
      setPasswordExpiredMessage("");
      setErrorMessage("");
      setPasswordRecentlyUsed(false);
      toast.success("Contraseña cambiada exitosamente");
    }
  }, [resetPasswordStates]);

  // Handle password reset errors
  useEffect(() => {
    if (resetPasswordStates.error) {
      const error = resetPasswordStates.error as any;
      const message = error.message || "";
      const httpCode = error.httpCode || error.status;
      
      if (message.includes("PASSWORD_RECENTLY_USED")) {
        setPasswordRecentlyUsed(true);
        setErrorMessage(
          "No puedes usar una contraseña que ya has utilizado recientemente. Por favor, elige una contraseña diferente."
        );
        const errorMsg = "Esta contraseña ya fue utilizada recientemente";
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      } else if (httpCode === 404) {
        const errorMsg = message || "Código de recuperación inválido";
        setErrorMessage(message || "El código ingresado es inválido o ya fue utilizado");
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      } else {
        const errorMsg = message || "Error al cambiar la contraseña";
        setErrorMessage(errorMsg);
        if (!shownToastsRef.current.has(errorMsg)) {
          shownToastsRef.current.add(errorMsg);
          toast.error(errorMsg);
        }
      }
      
      // Reset Formik submitting state to unlock the button
      if (passwordResetFormActions) {
        passwordResetFormActions.setSubmitting(false);
      }
      
      // Clear error state immediately to unlock the button
      dispatch({ type: RESET_PASSWORD_CLEAR_ERROR });
    }
  }, [resetPasswordStates.error, dispatch, passwordResetFormActions]);

  useEffect(() => {
    if (isAuthenticated) {
      // Save browser hash if returned from API
      if (authData?.browserHash || authData?.browser_hash) {
        localStorage.setItem("browserHash", authData.browserHash || authData.browser_hash);
      }
      navigate(from, { replace: true });
    }
    return () => {};
  }, [isAuthenticated]);

  useEffect(() => {
    if (!!userInformation) {
      navigate("/dashboard", { replace: true });
    }
    return () => {};
  }, [userInformation]);

  // Auto-focus effects
  useEffect(() => {
    if (showLogin && !showMfaChallenge && !openMfaConfig && loginEmailRef.current) {
      setTimeout(() => loginEmailRef.current?.focus(), 100);
    }
  }, [showLogin, showMfaChallenge, openMfaConfig]);

  useEffect(() => {
    if (showMfaChallenge && !showMfaRecovery && mfaTokenRef.current) {
      setTimeout(() => mfaTokenRef.current?.focus(), 100);
    }
  }, [showMfaChallenge, showMfaRecovery]);

  useEffect(() => {
    if (showMfaRecovery && mfaBackupRef.current) {
      setTimeout(() => mfaBackupRef.current?.focus(), 100);
    }
  }, [showMfaRecovery]);

  useEffect(() => {
    if (showPasswordRecovery && !showPasswordChange && recoveryEmailRef.current) {
      setTimeout(() => recoveryEmailRef.current?.focus(), 100);
    }
  }, [showPasswordRecovery, showPasswordChange]);

  useEffect(() => {
    if (showPasswordChange && changePasswordCodeRef.current) {
      setTimeout(() => changePasswordCodeRef.current?.focus(), 100);
    }
  }, [showPasswordChange]);

  return (
    <>
{openMfaConfig && (
  <MfaConfig
    userInfo={userInfoForMfa}
    setOpenConfigModals={setOpenMfaConfig}
    onMfaSetupComplete={() => {
      // Auto-login after MFA setup with the stored browser_hash
      const storedBrowserHash = localStorage.getItem("browserHash");
      if (storedBrowserHash && userInfoForMfa) {
        dispatch(signIn({
          email: userInfoForMfa.email,
          password: userInfoForMfa.password,
          browser_hash: storedBrowserHash,
        }));
      }
    }}
  />
)}

{showLogin && !showMfaChallenge && (
        <>
          <div>
            <h3>Bienvenido</h3>
            <p>Ingresá tu email y contraseña</p>
          </div>

          <Form
            id="login-form"
            rules={signInSchema}
            values={signInInitialValues}
            onSubmit={appSignIn}
            validateOnBlur={false}
            validateOnChange={false}
          >
            <Input
              type="email"
              name="email"
              placeholder="Ingrese su e-mail"
              options={{ label: "E-mail", marginBottom: 24 }}
              forwardRef={loginEmailRef}
            />

            <Input
              type="password"
              name="password"
              placeholder="Ingrese su contraseña"
              options={{ label: "Contraseña", marginBottom: 24 }}
            />

            <Center style={{ marginBottom: "24px" }}>
              <ErrorMessage visible={!!lockoutMessage}>
                {lockoutMessage}
              </ErrorMessage>
              <ErrorMessage visible={!!authError && !lockoutMessage && !!errorMessage}>
                {errorMessage}
              </ErrorMessage>
            </Center>

            <Center horizontal="center" vertical="center">
              <Submit
                form="login-form"
                isSubmmiting={authLoading || userLoading}
              >
                Iniciar sesión
              </Submit>
              <Button
                type="button"
                options={{
                  skin: "primary",
                  size: "lg",
                  type: "outline",
                }}
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setShowLogin(false);
                  setShowPasswordRecovery(true);
				  // @ts-ignore
                  setShowPasswordChange(false);
                  setLockoutMessage("");
                  setErrorMessage("");
                }}
              >
                Olvide mi contraseña
              </Button>
            </Center>
          </Form>
        </>
      )}

      {showMfaChallenge && (
        <>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            {showMfaRecovery ? (
              <>
                <h3>Recuperar cuenta</h3>
                <p>
                  Introduce el código de respaldo que recibiste al configurar la
                  autenticación de dos factores
                </p>
              </>
            ) : (
              <>
                <h3>Autenticación de dos factores</h3>
                <p>Ingresa el código de tu aplicación de autenticación</p>
              </>
            )}
          </div>

          <Formik
            initialValues={showMfaRecovery ? { backupCode: "" } : { token: "" }}
            validationSchema={showMfaRecovery ? mfaBackupSchema : mfaTokenSchema}
            onSubmit={(values, actions) => {
              handleMfaSubmit({ values, actions });
            }}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setErrors, setTouched }) => (
                <form id="mfa-form" onSubmit={handleSubmit}>
                  {showMfaRecovery ? (
                    <Input
                      type="text"
                      name="backupCode"
                      placeholder="Ingresa tu código de respaldo"
                      options={{ label: "Código de respaldo", marginBottom: 24 }}
                      value={values.backupCode}
                      error={errors.backupCode}
                      touched={touched.backupCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      forwardRef={mfaBackupRef}
                    />
                  ) : (
                    <>
                      <Input
                        type="text"
                        name="token"
                        placeholder="000000"
                        options={{ label: "Código de autenticación", marginBottom: 24 }}
                        value={values.token}
                        error={errors.token}
                        touched={touched.token}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          handleChange({ target: { name: 'token', value } });
                        }}
                        onBlur={handleBlur}
                        forwardRef={mfaTokenRef}
                      />

                      <div style={{ marginBottom: "24px" }}>
                        <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                            style={{ marginRight: "8px" }}
                          />
                          <span style={{ fontSize: "14px" }}>
                            Recordar este dispositivo por 30 días
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  <Center style={{ marginBottom: "16px" }}>
                    <ErrorMessage visible={!!authError}>
                      {errorMessage || "Código incorrecto"}
                    </ErrorMessage>
                  </Center>

                  <Center horizontal="center" vertical="center">
                    <Submit 
                      form="mfa-form" 
                      isSubmmiting={authLoading}
                    >
                      {showMfaRecovery ? "Recuperar cuenta" : "Verificar"}
                    </Submit>
                    <Button
                      type="button"
                      options={{
                        skin: "secondary",
                        size: "lg",
                        type: "outline",
                      }}
                      style={{ marginLeft: "10px" }}
                      onClick={() => {
                        setShowMfaChallenge(false);
                        setShowLogin(true);
                        setShowMfaRecovery(false);
                        setMfaToken("");
                        setMfaBackupCode("");
                      }}
                    >
                      Cancelar
                    </Button>
                  </Center>

                  {!showMfaRecovery && (
                    <Center style={{ marginTop: "16px" }}>
                      <Button
                        type="button"
                        options={{
                          skin: "primary",
                          size: "sm",
                          type: "link",
                        }}
                        onClick={() => {
                          setShowMfaRecovery(true);
                          setMfaToken("");
                          setErrors({});
                          setTouched({});
                          setErrorMessage("");
                        }}
                      >
                        ¿Perdiste tu teléfono? Usa un código de respaldo
                      </Button>
                    </Center>
                  )}

                  {showMfaRecovery && (
                    <Center style={{ marginTop: "16px" }}>
                      <Button
                        type="button"
                        options={{
                          skin: "primary",
                          size: "sm",
                          type: "link",
                        }}
                        onClick={() => {
                          setShowMfaRecovery(false);
                          setMfaBackupCode("");
                          setErrors({});
                          setTouched({});
                          setErrorMessage("");
                        }}
                      >
                        Volver a autenticación normal
                      </Button>
                    </Center>
                  )}
                </form>
            )}
          </Formik>
        </>
      )}

      {showPasswordRecovery && (
        <>
          <div>
            <h3>Recupera tu contraseña</h3>
            <p>
              {passwordExpiredMessage
                ? passwordExpiredMessage
                : "Ingresá tu e-mail"}
            </p>
          </div>

          {errorMessage && (
            <Center style={{ marginBottom: "24px" }}>
              <ErrorMessage visible={true}>{errorMessage}</ErrorMessage>
            </Center>
          )}

          <Form
            id="recover-code-form"
            rules={recoveryCodeSchema}
            values={recoveryCodeInitialValues}
            onSubmit={sendRecoveryCode}
            validateOnBlur={false}
            validateOnChange={false}
          >
            <Input
              type="email"
              name="email"
              placeholder="Ingrese su e-mail"
              options={{ label: "E-mail" }}
              forwardRef={recoveryEmailRef}
            />

            <Center horizontal="center" vertical="center">
              <Submit
                form="recover-code-form"
                isSubmmiting={recoverPasswordStates.loading}
              >
                Recuperar contraseña
              </Submit>

              <Button
                type="button"
                options={{
                  skin: "primary",
                  size: "lg",
                  type: "outline",
                }}
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  setShowLogin(true);
                  setShowPasswordRecovery(false);
				  //@ts-ignore
                  setShowPasswordChange(false);
                  setPasswordExpiredMessage("");
                  setErrorMessage("");
                  setLockoutMessage("");
                }}
              >
                cancelar
              </Button>
            </Center>
          </Form>
        </>
      )}

      {showPasswordChange && (
        <>
          <div>
            <h3>Recupera tu contraseña</h3>
            <p>
              Ingresá el código de recuperación que enviamos a tu correo
              electrónico.
            </p>
          </div>

          {errorMessage && (
            <Center style={{ marginBottom: "24px" }}>
              <ErrorMessage visible={true}>{errorMessage}</ErrorMessage>
            </Center>
          )}

          <Formik
            initialValues={{
              ...passwordRecoveryInitialValues,
              email: accountToRecover,
              code: codeFromUrl,
              verification: verificationFromUrl,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email("Email inválido").required("Este campo es requerido"),
              code: Yup.string().required("Este campo es requerido"),
              password: Yup.string()
                .required("Este campo es requerido")
                .min(8, "Debe tener al menos 8 caracteres")
                .matches(/[A-Z]/, "Debe contener al menos 1 letra mayúscula")
                .matches(/[0-9]/, "Debe contener al menos 1 número")
                .matches(/[^A-Za-z0-9]/, "Debe contener al menos 1 símbolo"),
              "confirm-password": Yup.string()
                .required("Este campo es requerido")
                .oneOf([Yup.ref("password")], "Las contraseñas deben coincidir"),
            })}
            onSubmit={(values, actions) => {
              changePassword({ values, actions });
            }}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
              const password = values.password || "";
              const hasMinLength = password.length >= 8;
              const hasUpperCase = /[A-Z]/.test(password);
              const hasNumber = /[0-9]/.test(password);
              const hasSymbol = /[^A-Za-z0-9]/.test(password);

              return (
                <form onSubmit={handleSubmit}>
                  <input type="hidden" name="verification" value={verificationFromUrl || recoverPasswordInformation?.result?.verification || ''} />

                  <Input
                    type="text"
                    readOnly
                    name="email"
                    options={{ label: "Email" }}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Input
                    type="text"
                    name="code"
                    placeholder="xxxxxx"
                    options={{ label: "Código de recuperación" }}
                    autocomplete="off"
                    forwardRef={changePasswordCodeRef}
                    value={values.code}
                    error={errors.code}
                    touched={touched.code}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Input
                    type="password"
                    name="password"
                    placeholder="Ingrese su contraseña"
                    options={{ label: "Nueva contraseña" }}
                    autocomplete="off"
                    value={values.password}
                    error={errors.password}
                    touched={touched.password}
                    onChange={(e) => {
                      handleChange(e);
                      setPasswordRecentlyUsed(false);
                      setErrorMessage("");
                    }}
                    onBlur={handleBlur}
                  />

                  <Input
                    type="password"
                    name="confirm-password"
                    placeholder="Ingrese su contraseña"
                    options={{ label: "Confirmar contraseña" }}
                    autocomplete="off"
                    value={values["confirm-password"]}
                    error={errors["confirm-password"]}
                    touched={touched["confirm-password"]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "16px" }}>
                    <p style={{ marginBottom: "8px" }}>
                      <strong>Requisitos de la contraseña:</strong>
                    </p>
                    <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
                      <li style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px", fontSize: "14px", color: passwordRecentlyUsed ? "#ef4444" : "#666" }}>
                          {passwordRecentlyUsed ? "✗" : "⚪"}
                        </span>
                        <span style={{ color: passwordRecentlyUsed ? "#ef4444" : "#666" }}>
                          No debes usar una contraseña usada recientemente
                        </span>
                      </li>
                      <li style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px", fontSize: "14px", color: hasMinLength ? "#10b981" : "#ef4444" }}>
                          {hasMinLength ? "✓" : "✗"}
                        </span>
                        <span style={{ color: hasMinLength ? "#10b981" : "#666" }}>
                          Debe tener al menos 8 caracteres
                        </span>
                      </li>
                      <li style={{ marginBottom: "4px", display: "flex", alignItems: "center" }}>
                        <span style={{ marginRight: "8px", fontSize: "14px", color: (hasUpperCase && hasSymbol && hasNumber) ? "#10b981" : "#ef4444" }}>
                          {(hasUpperCase && hasSymbol && hasNumber) ? "✓" : "✗"}
                        </span>
                        <span style={{ color: (hasUpperCase && hasSymbol && hasNumber) ? "#10b981" : "#666" }}>
                          Debe contener al menos 1 letra mayúscula, 1 símbolo y 1 número
                        </span>
                      </li>
                    </ul>
                  </div>

                  <Center horizontal="center" vertical="center">
                    <Button
                      type="submit"
                      options={{
                        skin: "primary",
                        size: "lg",
                        type: "filled",
                      }}
                      disabled={isSubmitting || resetPasswordStates.loading}
                    >
                      Cambiar contraseña
                    </Button>

                    <Button
                      type="button"
                      options={{
                        skin: "primary",
                        size: "lg",
                        type: "outline",
                      }}
                      style={{ marginLeft: "10px" }}
                      onClick={() => {
                        setShowLogin(true);
                        setShowPasswordRecovery(false);
						//@ts-ignore
                        setShowPasswordChange(false);
                        setPasswordExpiredMessage("");
                        setErrorMessage("");
                        setLockoutMessage("");
                        setPasswordRecentlyUsed(false);
                      }}
                    >
                      cancelar
                    </Button>
                  </Center>
                </form>
              );
            }}
          </Formik>
        </>
      )}
    </>
  );
};

const state = ({ authStore, userStore }) => {
  const { signin, recoverPassword, resetPassword } = authStore;
  const { states: authStates, data: authData } = signin;
  const { states: recoverPasswordStates, data: recoverPasswordInformation } =
    recoverPassword;
  const { states: resetPasswordStates } = resetPassword;
  const { data: userInformation, states: userStates } = userStore.information;
  return {
    authStates: { ...authStates, data: authData },
    recoverPasswordStates,
    recoverPasswordInformation,
    resetPasswordStates,
    userStates,
    userInformation,
  };
};

export default connect(state)(Page);
